import { lazy, Suspense, useMemo, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, Download, Footprints, MapPin, Navigation } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Markdown } from '../ui/markdown';
import { Skeleton } from '../ui/skeleton';
import { CragGradeChart } from './CragGradeChart';
import { CragMaintainersCard } from './CragMaintainersCard';
import { formatGradeRange, gradeRanges } from './grade-range';
import type { Crag, TrackPoint } from '../../types/crag';
import type { Climb } from '../../types/climb';
import type { Zone, ZoneExposure } from '../../types/zone';

// Leaflet and its tiles are a couple of hundred kilobytes; a crag with no
// track recorded never loads them, and neither does anyone reading the sectors.
const CragApproachMap = lazy(() => import('./CragApproachMap'));

/** The order of the summary card's columns, left to right. */
const SUMMARY_KEYS = ['climbs', 'grades', 'exposure'] as const;

/** Stands in for a column nobody has filled in, so the row keeps its shape. */
const UNKNOWN = '—';

/**
 * A coordinate pair, when both halves are there. Rails serializes a decimal
 * column as a string, so what arrives is `"45.891"` rather than `45.891` —
 * fine in a URL, not fine for a map that does arithmetic on it.
 */
function coordinate(lat: unknown, lng: unknown): TrackPoint | null {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (lat == null || lng == null || Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
  return [latitude, longitude];
}

interface MapSectionProps {
  title: string;
  icon: ComponentType<{ className?: string }>;
  /** "lat,lng" — both a map query and a navigation destination. */
  coordinates: string;
  /** The language the map's own labels come back in. */
  language: string;
  /** Present only where driving there is the thing you actually do. */
  navigateLabel?: string;
}

/**
 * A point on the map, embedded rather than linked so the reader can see where
 * it is without leaving the crag.
 */
function MapSection({ title, icon: Icon, coordinates, language, navigateLabel }: MapSectionProps) {
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-sm font-medium">
        <Icon className="h-4 w-4" />
        {title}
      </h3>
      {/* The embed needs no API key, which keeps the map working wherever this
          is deployed. `loading="lazy"` so a page opened on the info tab does not
          pay for it before it is scrolled to. */}
      <iframe
        title={title}
        src={`https://www.google.com/maps?q=${coordinates}&hl=${language}&z=16&output=embed`}
        className="h-56 w-full rounded-lg border"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      {navigateLabel && (
        <Button asChild variant="outline" size="sm" className="gap-2">
          {/* `dir/?api=1` is the cross-platform link: it hands off to the Maps
              app on a phone and opens directions in the browser on a desktop,
              which is what "start the navigator" means on each. */}
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${coordinates}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Navigation className="h-4 w-4" />
            {navigateLabel}
          </a>
        </Button>
      )}
    </section>
  );
}

interface CragInfoPanelProps {
  crag: Crag;
  /** Every climb of the crag, not just one sector's — the counts are the crag's. */
  climbs: Climb[];
  sectors: Zone[];
  /** Opens a sector's tab from its chip in the sectors list. */
  onSelectSector: (sectorId: string) => void;
}

/**
 * What the crag is, before you get to the topo: the write-up, which way it
 * faces, how much there is and how hard, where to park and how to walk in.
 *
 * It reads the crag and the climbs the page already has rather than asking the
 * API for counts — the tabs beside it are drawn from the same two lists, so a
 * separate endpoint would be a second version of the truth.
 */
export function CragInfoPanel({ crag, climbs, sectors, onSelectSector }: CragInfoPanelProps) {
  const { t, i18n } = useTranslation();

  const { routes, boulders } = useMemo(() => ({
    routes: climbs.filter((climb) => climb.type === 'Route'),
    boulders: climbs.filter((climb) => climb.type === 'Boulder'),
  }), [climbs]);

  // Two points, and they are rarely the same one: the cliff says where the
  // climbing is, the parking is the only one worth driving to. The API sends
  // decimals, which ride back as strings — fine for a map query, but the
  // approach map wants numbers.
  const positionPoint = coordinate(crag.latitude, crag.longitude);
  const parkingPoint = coordinate(crag.parking_latitude, crag.parking_longitude);
  const position = positionPoint ? positionPoint.join(',') : null;
  const parking = parkingPoint ? parkingPoint.join(',') : null;

  // A track only counts once it is a line: one point cannot be walked along.
  const track = crag.approach_track && crag.approach_track.length > 1
    ? crag.approach_track
    : null;

  /** Whether there is anything to say about getting there at all. */
  const hasDirections = !!(position || parking || crag.approach_gpx_url);

  // What the whole cliff amounts to, read off the same climbs the sectors are
  // read off — there is no second version of the truth to drift from.
  const cragRanges = useMemo(() => gradeRanges(climbs), [climbs]);

  // The crag's aspect is its sectors' aspects, deduplicated: a cliff that faces
  // south-west everywhere says so once, one with a shaded flank says both.
  const cragExposures = useMemo(
    () => [...new Set(sectors.map((sector) => sector.exposure).filter(Boolean))] as ZoneExposure[],
    [sectors]
  );

  const climbsBySectorId = useMemo(() => {
    const groups = new Map<string, Climb[]>();
    for (const climb of climbs) {
      if (!climb.zone?.id) continue;
      const group = groups.get(climb.zone.id);
      if (group) group.push(climb);
      else groups.set(climb.zone.id, [climb]);
    }
    return groups;
  }, [climbs]);

  // Always three columns, never two: a crag with no aspect recorded still has a
  // column for it, because a row that changes shape from crag to crag stops
  // being something you can read at a glance.
  const summary = {
    climbs: { label: t('cragInfo.climbsCount'), value: String(climbs.length) },
    grades: {
      label: t('cragInfo.difficulty'),
      value: cragRanges.length > 0 ? cragRanges.map(formatGradeRange).join(' · ') : UNKNOWN,
    },
    exposure: {
      label: t('cragInfo.exposure'),
      value: cragExposures.length > 0
        ? cragExposures.map((exposure) => t(`cragInfo.exposures.${exposure}`)).join(', ')
        : UNKNOWN,
    },
  };

  return (
    <div className="space-y-4">
      {/* What the cliff amounts to, at a glance and on its own: three numbers
          read straight off the climbs the page already has. Three equal columns
          so the eye lands on each in turn rather than reading a sentence. */}
      <Card>
        <CardContent className="grid grid-cols-3 divide-x p-0">
          {SUMMARY_KEYS.map((key) => (
            <div key={key} className="px-3 py-3 text-center">
              <p className="text-xs text-muted-foreground">{summary[key].label}</p>
              <p className="mt-1 truncate text-base font-semibold">{summary[key].value}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {crag.description ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h3 className="text-sm font-medium">{t('cragInfo.description')}</h3>
            <Markdown>{crag.description}</Markdown>
          </CardContent>
        </Card>
      ) : null}

      {/* Who to thank for the bolts, and who a report reaches. Above the
          sectors because it is about the crag as a whole, like the write-up. */}
      <CragMaintainersCard maintainers={crag.maintainers ?? []} />

      {sectors.length > 0 && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h3 className="text-sm font-medium">{t('cragInfo.sectors')}</h3>

            {/* The same three things, sector by sector. Reading down the list is
                how you pick where to go today: on one crag the sunny wall and
                the shaded one are twenty metres apart, and so are the 5c slab
                and the 8a roof. */}
            <ul className="-mx-4 divide-y border-y">
              {sectors.map((sector) => {
                const sectorClimbs = climbsBySectorId.get(sector.id) ?? [];
                const ranges = gradeRanges(sectorClimbs);
                const details = [
                  ranges.length > 0 ? ranges.map(formatGradeRange).join(' · ') : null,
                  sector.exposure ? t(`cragInfo.exposures.${sector.exposure}`) : null,
                ].filter(Boolean);

                return (
                  <li key={sector.id}>
                    <button
                      type="button"
                      onClick={() => onSelectSector(sector.id)}
                      className="flex w-full items-baseline justify-between gap-3 px-4 py-2 text-left text-sm transition-colors hover:bg-accent"
                    >
                      <span className="truncate">{sector.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {details.length > 0 ? details.join(' · ') : t('cragInfo.sectorNothingRecorded')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* One chart per discipline: a crag grades its boulders and its routes on
          two different scales, and a single axis cannot carry both. */}
      <CragGradeChart climbs={routes} title={t('cragInfo.routeGrades')} />
      <CragGradeChart climbs={boulders} title={t('cragInfo.boulderGrades')} />

      {/* Getting there is one question with three answers — where it is, where
          the car stops, how you walk the last bit — so they fold away together
          rather than as three sections you scroll past on every visit.

          Closed until asked for, which also means the three maps inside cost
          nothing to a reader who is here for the routes: the Google embeds and
          Leaflet's own chunk are only fetched once this is opened. */}
      {hasDirections && (
        <Accordion type="single" collapsible>
          <AccordionItem value="directions" className="rounded-lg border bg-card px-3">
            <AccordionTrigger className="text-sm">
              <span className="flex items-center gap-1.5">
                <Navigation className="h-4 w-4" />
                {t('cragInfo.directions')}
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-6 pt-2">
              {/* Where the rock is — to look at, not to drive to. */}
              {position && (
                <MapSection
                  title={t('cragInfo.position')}
                  icon={MapPin}
                  coordinates={position}
                  language={i18n.language}
                />
              )}

              {/* Where the car goes, and the only point navigation is ever
                  started to: a car cannot be driven to the foot of a cliff. */}
              {parking && (
                <MapSection
                  title={t('cragInfo.parking')}
                  icon={Car}
                  coordinates={parking}
                  language={i18n.language}
                  navigateLabel={t('cragInfo.navigate')}
                />
              )}

              {crag.approach_gpx_url && (
                <section className="space-y-2">
                  <h3 className="flex items-center gap-1.5 text-sm font-medium">
                    <Footprints className="h-4 w-4" />
                    {t('cragInfo.approach')}
                  </h3>

                  {/* The walk drawn, before the walk downloaded: the file
                      answers "can I follow this on my phone", the map answers
                      "how long is it and where does it start", which is the
                      question you have first.

                      Loaded on demand — Leaflet is a couple of hundred
                      kilobytes a crag with no track should never pay for. */}
                  {track && (
                    <Suspense fallback={<Skeleton className="h-64 w-full rounded-lg" />}>
                      <CragApproachMap
                        track={track}
                        parking={parkingPoint}
                        crag={positionPoint}
                      />
                    </Suspense>
                  )}

                  <p className="text-sm text-muted-foreground">{t('cragInfo.approachHint')}</p>
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <a href={crag.approach_gpx_url} download={crag.approach_gpx_filename ?? undefined}>
                      <Download className="h-4 w-4" />
                      {crag.approach_gpx_filename || t('cragInfo.downloadGpx')}
                    </a>
                  </Button>
                </section>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {!crag.description && !hasDirections && (crag.maintainers?.length ?? 0) === 0 && (
        <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t('cragInfo.empty')}
        </p>
      )}
    </div>
  );
}
